import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ipoService } from '../../api/ipoService';
import { mapIpoDetailResponse } from '../../utils/ipoDataMapper';
import { mapWithConcurrency } from '../../api/mapWithConcurrency';

/**
 * The DRHP filings list: GET /api/core/ipos/drhp-tracker. One request, enough
 * for a filings table (company, board, regulatory status, filing dates).
 *
 * options.maxAgeMs: skip the request when data this recent is already loaded
 */
export const fetchDrhpRegistry = createAsyncThunk(
  'drhp/fetchDrhpRegistry',
  async (_options, { rejectWithValue }) => {
    try {
      const response = await ipoService.getDrhpTracker();
      if (response?.success === false) {
        return rejectWithValue(response.error || 'Failed to fetch DRHP filings');
      }
      const records = Array.isArray(response?.data) ? response.data : [];
      const total = typeof response?.total === 'number' ? response.total : records.length;
      return { records, total };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch DRHP filings');
    }
  },
  {
    condition: ({ maxAgeMs = 0 } = {}, { getState }) => {
      const { status, fetchedAt } = getState().drhp;
      // Skip duplicate requests while one is in flight (e.g. StrictMode double effects)
      if (status === 'loading') return false;
      if (maxAgeMs && status === 'succeeded' && fetchedAt && Date.now() - fetchedAt < maxAgeMs) {
        return false;
      }
      return true;
    },
  }
);

/**
 * The extracted DRHP details behind the list: GET /api/core/ipos/{id} for the
 * filings whose analysis is approved (pipeline_status "approved"), which carry
 * the sector, lead managers and issue structure. One request per filing, and
 * three quarters of the tracker is approved, so a caller that only shows a few
 * rows passes the ids it is going to render instead of paying for all of them.
 *
 * options.ids: only these filings, rather than every approved one
 * options.maxAgeMs: passed through to the list fetch
 */
// The backend answers a large burst with HTTP 429, so details go out a few at a time
const DETAIL_CONCURRENCY = 5;

export const fetchDrhpDetails = createAsyncThunk(
  'drhp/fetchDrhpDetails',
  async (options = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      await dispatch(fetchDrhpRegistry(options));
      const { records, detailsById } = getState().drhp;
      const only = options.ids ? new Set(options.ids.map(String)) : null;
      const wanted = records.filter(
        (record) =>
          record.pipeline_status === 'approved' && !detailsById[record.id] && (!only || only.has(String(record.id)))
      );
      if (!wanted.length) return {};

      const results = await mapWithConcurrency(wanted, (record) => ipoService.getIpoById(record.id), {
        limit: DETAIL_CONCURRENCY,
      });
      const loaded = {};
      results.forEach((result, idx) => {
        if (result.status !== 'fulfilled') return;
        const detail = mapIpoDetailResponse(result.value);
        if (detail) loaded[wanted[idx].id] = detail;
      });
      return loaded;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch DRHP details');
    }
  },
  {
    condition: (_options, { getState }) => getState().drhp.detailsStatus !== 'loading',
  }
);

const initialState = {
  // Tracker items (GET /api/core/ipos/drhp-tracker)
  records: [],
  total: 0,
  fetchedAt: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,

  // Mapped details for analysed DRHPs, loaded separately by fetchDrhpDetails
  detailsById: {},
  detailsStatus: 'idle',
  detailsError: null,
};

export const drhpSlice = createSlice({
  name: 'drhp',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDrhpRegistry.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchDrhpRegistry.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.records = action.payload.records;
        state.total = action.payload.total;
        state.fetchedAt = Date.now();
      })
      .addCase(fetchDrhpRegistry.rejected, (state, action) => {
        // Keep the previous registry so a failed refresh doesn't blank the page
        state.status = 'failed';
        state.error = action.payload || 'Failed to load DRHP filings';
      })

      .addCase(fetchDrhpDetails.pending, (state) => {
        state.detailsStatus = 'loading';
        state.detailsError = null;
      })
      .addCase(fetchDrhpDetails.fulfilled, (state, action) => {
        state.detailsStatus = 'succeeded';
        state.detailsById = { ...state.detailsById, ...action.payload };
      })
      .addCase(fetchDrhpDetails.rejected, (state, action) => {
        state.detailsStatus = 'failed';
        state.detailsError = action.payload || 'Failed to load DRHP details';
      });
  },
});

export default drhpSlice.reducer;
